import { createAvexaClient, type AvexaClient } from '@avexa/api'

/**
 * The one Supabase client the superadmin panel uses.
 *
 * Built by `createAvexaClient` from `@avexa/api` (Docs/03, "Data-access layer")
 * so the website, this panel and the mobile app cannot drift on session
 * handling — and so that the key-safety check in that factory runs here too:
 * `.env` is committed in this repository, the anon and service-role keys sit
 * four lines apart in it, and a copy-paste that swaps them produces a client
 * that works while bypassing every RLS policy in the schema. In a panel whose
 * entire job is privileged writes, that is the single worst mistake available,
 * and the factory refuses it.
 *
 * NO COMPONENT CALLS `.from()`. Every query in this app goes through a named
 * function in `@avexa/api` with this client as its first argument.
 *
 * HOW THIS DIFFERS FROM `apps/web/src/lib/avexa.ts`. The marketing site is
 * anonymous and read-only, so it sets `persistSession: false` — a token in
 * localStorage that nothing reads is only a liability. This panel has a real
 * operator behind it: the session MUST persist and MUST refresh, or a moderator
 * is signed out by a page reload halfway through a queue.
 *
 * Created once, lazily: an unconfigured `.env` must fail where the session
 * bootstrap can report it in the UI, not at module-import time where it blanks
 * the page and leaves an operator with a white screen and no explanation.
 */

let client: AvexaClient | null = null

export const isAvexaConfigured = (): boolean =>
  Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY)

export const getAvexaClient = (): AvexaClient => {
  if (client) return client

  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  if (!url || !anonKey) {
    throw new Error(
      'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env',
    )
  }

  client = createAvexaClient(url, anonKey, {
    // A moderator works through a queue across reloads. Without this they are
    // signed out by F5, which is the one thing that makes a panel unusable.
    persistSession: true,
    autoRefreshToken: true,
    // Nothing in this panel issues a link-based flow yet: only email + password
    // is enabled on the project (CLAUDE.md § 7). Flip to true the day password
    // recovery or a magic link lands, because that is the day auth-js needs to
    // read the token out of the URL fragment.
    detectSessionInUrl: false,
    // Namespaced so a future admin build sharing an origin with the website
    // cannot read or clobber the other's session.
    storageKey: 'avexa.admin.auth',
    headers: { 'x-avexa-surface': 'admin' },
  })

  return client
}
